// Selector de SKU con búsqueda (patrón combobox del portal): se teclea SKU, marca o modelo y se elige.
import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAnchoredPanel } from '@/shared/hooks/useAnchoredPanel';

export interface SkuOption {
    value: string;
    label: string;
    /** Texto por el que se encuentra la opción: SKU, marca, modelo y nombre. */
    keywords: string;
}

const PANEL_MAX_HEIGHT = 260;

export const FIELD_CLASS =
    'h-9 w-full rounded-md border border-input bg-background px-3 text-[13px] outline-none placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/25 disabled:cursor-not-allowed disabled:opacity-50';

/** Minúsculas y sin acentos, para que «galaxy» encuentre «Galaxy» y «telefono» a «Teléfono». */
function normalize(text: string): string {
    return text
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .toLowerCase();
}

/** Opciones que contienen todas las palabras tecleadas. */
export function filterSkuOptions(options: readonly SkuOption[], query: string): SkuOption[] {
    const terms = normalize(query).split(/\s+/).filter(Boolean);
    if (terms.length === 0) return [...options];
    return options.filter((option) => {
        const haystack = normalize(`${option.keywords} ${option.label}`);
        return terms.every((term) => haystack.includes(term));
    });
}

export function SkuCombobox({
    label,
    value,
    onChange,
    options,
    placeholder,
    error,
    disabled,
}: {
    label: string;
    value: string;
    onChange: (value: string) => void;
    options: readonly SkuOption[];
    placeholder?: string;
    error?: string;
    disabled?: boolean;
}) {
    const inputId = useId();
    const listboxId = useId();
    const inputRef = useRef<HTMLInputElement>(null);
    const optionRefs = useRef<(HTMLLIElement | null)[]>([]);
    /** `null` muestra la opción elegida; un texto es lo que el usuario está buscando. */
    const [query, setQuery] = useState<string | null>(null);
    const [open, setOpen] = useState(false);
    const [highlight, setHighlight] = useState(-1);

    const selected = options.find((option) => option.value === value);
    const results = filterSkuOptions(options, query ?? '');
    const active = highlight < results.length ? highlight : -1;
    const expanded = open && !disabled;
    const anchor = useAnchoredPanel(expanded, inputRef, PANEL_MAX_HEIGHT);

    useEffect(() => {
        if (active < 0) return;
        optionRefs.current[active]?.scrollIntoView?.({ block: 'nearest' });
    }, [active]);

    const close = () => {
        setOpen(false);
        setQuery(null);
        setHighlight(-1);
    };

    const choose = (option: SkuOption) => {
        onChange(option.value);
        close();
    };

    const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Escape') {
            if (!expanded) return;
            event.preventDefault();
            close();
            return;
        }
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            if (results.length === 0) return;
            const from = expanded ? active : -1;
            const next = event.key === 'ArrowDown' ? from + 1 : from - 1;
            setOpen(true);
            setHighlight(next < 0 ? results.length - 1 : next % results.length);
            return;
        }
        if (event.key === 'Enter' && expanded) {
            // Enter con la lista abierta elige; nunca manda el formulario.
            event.preventDefault();
            if (active >= 0) choose(results[active]);
            else if (results.length === 1) choose(results[0]);
        }
    };

    return (
        <div className="space-y-1.5">
            <label htmlFor={inputId} className="block text-sm font-medium text-foreground">
                {label}
            </label>
            <div className="relative">
                <input
                    ref={inputRef}
                    id={inputId}
                    type="text"
                    role="combobox"
                    aria-expanded={expanded}
                    aria-controls={expanded ? listboxId : undefined}
                    aria-autocomplete="list"
                    aria-activedescendant={
                        expanded && active >= 0 ? `${listboxId}-${String(active)}` : undefined
                    }
                    aria-invalid={error ? true : undefined}
                    autoComplete="off"
                    disabled={disabled}
                    placeholder={placeholder}
                    value={query ?? selected?.label ?? ''}
                    className={cn(FIELD_CLASS, 'pr-8', error && 'border-destructive')}
                    onClick={() => {
                        setOpen(true);
                    }}
                    onFocus={(event) => {
                        event.target.select();
                    }}
                    onBlur={close}
                    onKeyDown={onKeyDown}
                    onChange={(event) => {
                        setQuery(event.target.value);
                        setHighlight(-1);
                        setOpen(true);
                    }}
                />
                <ChevronDown
                    aria-hidden
                    className="pointer-events-none absolute right-2.5 top-1/2 size-4 -translate-y-1/2 opacity-50"
                />
            </div>
            {error ? <p className="text-xs text-destructive">{error}</p> : null}
            {expanded &&
                createPortal(
                    <ul
                        id={listboxId}
                        role="listbox"
                        aria-label={label}
                        style={{
                            position: 'fixed',
                            left: anchor?.left ?? 0,
                            width: anchor?.width ?? 0,
                            top: anchor?.top,
                            bottom: anchor?.bottom,
                            maxHeight: Math.min(
                                anchor?.maxHeight ?? PANEL_MAX_HEIGHT,
                                PANEL_MAX_HEIGHT,
                            ),
                        }}
                        className="z-50 overflow-y-auto rounded-md border border-border bg-popover py-1 text-popover-foreground shadow-lg"
                    >
                        {results.length === 0 ? (
                            <li className="px-3 py-2 text-[12px] text-muted-foreground">
                                Ningún SKU coincide. Prueba con el SKU, la marca o el modelo.
                            </li>
                        ) : (
                            results.map((option, index) => (
                                // El teclado vive en el input (aria-activedescendant).
                                // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                                <li
                                    key={option.value}
                                    id={`${listboxId}-${String(index)}`}
                                    ref={(element) => {
                                        optionRefs.current[index] = element;
                                    }}
                                    role="option"
                                    aria-selected={option.value === value}
                                    className={cn(
                                        'cursor-pointer px-3 py-1.5 text-[12.5px]',
                                        index === active ? 'bg-primary/10' : 'hover:bg-accent/50',
                                        option.value === value && 'font-semibold',
                                    )}
                                    // Sin esto el clic le quita el foco al input y la lista cierra antes de elegir.
                                    onMouseDown={(event) => {
                                        event.preventDefault();
                                    }}
                                    onMouseEnter={() => {
                                        setHighlight(index);
                                    }}
                                    onClick={() => {
                                        choose(option);
                                    }}
                                >
                                    {option.label}
                                </li>
                            ))
                        )}
                    </ul>,
                    document.body,
                )}
        </div>
    );
}
